/**
 * Grade computation helpers shared by the Grading Setup and Grades pages.
 * Pure functions only — no React, no API.
 */

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function subActivityGrade(score, itemCount) {
  const s = Number(score) || 0;
  const i = Number(itemCount) || 0;
  if (i <= 0) return 0;
  return round2((s / i) * 100);
}

export function totalPeriodPercent(periods) {
  return round2((periods || []).reduce((a, p) => a + (Number(p.period_percentage) || 0), 0));
}

export function totalComponentPercent(components) {
  return round2((components || []).reduce((a, c) => a + (Number(c.component_percentage) || 0), 0));
}

/**
 * For a single component: the average of all task grades that have a score.
 * Returns null if no graded task yet.
 */
export function componentAverage(tasks, scoreByTask) {
  const grades = [];
  for (const t of tasks || []) {
    const s = scoreByTask?.get?.(t.id);
    if (s != null) grades.push(subActivityGrade(s, t.item_count));
  }
  if (!grades.length) return null;
  return round2(grades.reduce((a, g) => a + g, 0) / grades.length);
}

/**
 * Weighted sum of component averages for one category.
 * tasksByComponent: { [component_id]: AssessmentTask[] }
 */
export function categoryGradeForStudent(components, tasksByComponent, scoreByTask) {
  if (!components?.length) return null;
  let weightedSum = 0;
  let anyData = false;
  for (const c of components) {
    const avg = componentAverage(tasksByComponent?.[c.id] || [], scoreByTask);
    if (avg != null) {
      anyData = true;
      weightedSum += avg * (Number(c.component_percentage) || 0) / 100;
    }
  }
  return anyData ? round2(weightedSum) : null;
}

/** Period grade = average of all category grades for that period (nulls dropped). */
export function periodGradeAvgCategories(categoryGrades) {
  const valid = (categoryGrades || []).filter((g) => g != null);
  if (!valid.length) return null;
  return round2(valid.reduce((a, g) => a + g, 0) / valid.length);
}

/** Final = weighted sum using period_percentage.
 *  Weights of ungraded periods are redistributed proportionally across the
 *  graded ones so a card with only 1 of 3 terms graded reflects that term
 *  fully instead of contributing only 33 % of its value.
 */
export function finalGradeForStudent(periodGrades) {
  const graded = (periodGrades || []).filter((p) => p.grade != null);
  if (!graded.length) return null;
  const weightSum = graded.reduce((s, p) => s + (Number(p.period_percentage) || 0), 0);
  // If the graded periods carry no weight (all zero), fall back to a plain
  // average so we never divide by zero.
  if (weightSum <= 0) {
    return round2(graded.reduce((s, p) => s + p.grade, 0) / graded.length);
  }
  const weighted = graded.reduce(
    (s, p) => s + p.grade * ((Number(p.period_percentage) || 0) / weightSum),
    0,
  );
  return round2(weighted);
}

export function findScaleMatch(percent, ranges) {
  if (percent == null || !ranges?.length) return null;
  return ranges.find((r) => {
    const min = r.min_percentage;
    const max = r.max_percentage;
    if (min == null || max == null) return false;
    return percent >= min && percent <= max;
  }) || null;
}

export function formatGradeWithScale(percent, scaleType, ranges) {
  if (percent == null) return "—";
  if (scaleType === "percentage") return `${percent.toFixed(2)}%`;
  let m = findScaleMatch(percent, ranges);
  // Values that land in a gap between ranges (e.g. 89.46 with ranges 85-89
  // and 90-95) get rounded to the nearest integer and retried so teachers
  // see the intuitive letter grade rather than a snapped outlier.
  if (!m) m = findScaleMatch(Math.round(percent), ranges);
  if (m) {
    return m.description ? `${m.code_or_value} (${m.description})` : m.code_or_value;
  }
  // Non-percentage scale but the value truly fell outside every defined
  // range even after rounding. Snap to the closest range by proximity so a
  // letter-based section always renders as letters (never as raw percent).
  const withRanges = (ranges || []).filter(
    (r) => r.min_percentage != null && r.max_percentage != null,
  );
  if (withRanges.length) {
    const sorted = [...withRanges].sort(
      (a, b) => (a.min_percentage ?? 0) - (b.min_percentage ?? 0),
    );
    let pick = sorted[0];
    let bestDist = Infinity;
    for (const r of sorted) {
      const dist = percent < r.min_percentage
        ? r.min_percentage - percent
        : percent > r.max_percentage
          ? percent - r.max_percentage
          : 0;
      if (dist < bestDist) { bestDist = dist; pick = r; }
    }
    return pick.description ? `${pick.code_or_value} (${pick.description})` : pick.code_or_value;
  }
  // Truly no ranges defined — fall back to raw percentage so the value is
  // still visible instead of showing an em-dash.
  return `${percent.toFixed(2)}%`;
}

/** Validate a list of scale ranges; returns a string error or null on success. */
export function validateScaleRanges(scaleType, ranges, opts = {}) {
  const needsDesc = scaleType === "custom" && opts.customDescEnabled;
  const needsRange = scaleType !== "custom" || opts.customRangeEnabled;
  if (!ranges?.length) return null;
  for (const r of ranges) {
    if (!r.code_or_value?.toString().trim()) return "Every scale range needs a code/value";
    if (needsDesc && !r.description?.trim()) return `Description required for "${r.code_or_value}"`;
    if (needsRange) {
      const min = Number(r.min_percentage);
      const max = Number(r.max_percentage);
      if (Number.isNaN(min) || Number.isNaN(max)) return `Min/Max required for "${r.code_or_value}"`;
      if (min < 0 || min > 100 || max < 0 || max > 100) return `Percentages must be 0-100`;
      if (min > max) return `Min > Max for "${r.code_or_value}"`;
    }
  }
  // No-overlap check
  if (needsRange) {
    const sorted = [...ranges].sort((a, b) => (a.min_percentage ?? 0) - (b.min_percentage ?? 0));
    for (let i = 1; i < sorted.length; i++) {
      if ((sorted[i].min_percentage ?? 0) <= (sorted[i - 1].max_percentage ?? -1)) {
        return `Overlapping ranges: "${sorted[i - 1].code_or_value}" and "${sorted[i].code_or_value}"`;
      }
    }
  }
  return null;
}

export function directPercentEquivalent(value, code, scaleType, ranges, customRangeEnabled) {
  if (scaleType === "percentage") return value != null ? Number(value) : null;
  if (!code) return null;
  const m = ranges?.find((r) => String(r.code_or_value) === String(code));
  if (!m) return null;
  if (scaleType === "custom" && !customRangeEnabled) return null;
  if (m.min_percentage == null || m.max_percentage == null) return null;
  return round2((m.min_percentage + m.max_percentage) / 2);
}

export function directDisplay(value, code, scaleType, ranges, customDescEnabled) {
  if (scaleType === "percentage") return value != null ? `${Number(value).toFixed(2)}%` : "—";
  if (!code) return "—";
  if (scaleType !== "custom") return code;
  const m = ranges?.find((r) => String(r.code_or_value) === String(code));
  if (!m) return code;
  return customDescEnabled && m.description ? `${code} (${m.description})` : code;
}
